/**
 * El cliente de la orden: a nombre de quien sale es a nombre de quien se
 * factura.
 *
 * Antes había dos campos —un desplegable de terceros y un «nombre si no está
 * registrado»—. El nombre suelto no sirve para facturar, así que la orden se
 * hacía, el carro se entregaba, y solo al final aparecía «para facturar hace
 * falta el cliente»: tarde, con el cliente ya fuera del lavadero.
 *
 * Ahora es un solo campo. Se busca por nombre o por documento, y si quien
 * entra no está, se crea desde aquí mismo con lo que hace falta para
 * facturarlo —persona natural con cédula o empresa con NIT—.
 */
import React, { useState } from 'react';
import {
  Autocomplete, TextField, Box, Typography, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, Stack, MenuItem, Alert, ToggleButton, ToggleButtonGroup,
  createFilterOptions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import api from '../api';
import { CampoCorreo, CampoTelefono } from './CamposContacto';
import { errorCorreo, errorTelefono } from '../contacto';
import { useEsMovil, FILA, ancho } from '../estilos/responsivo';

const filtrar = createFilterOptions({
  stringify: (t) => `${t.razon_social} ${t.numero_documento || ''}`,
});

const DOCS_NATURAL = [
  ['CC', 'Cédula de ciudadanía'],
  ['CE', 'Cédula de extranjería'],
  ['PAS', 'Pasaporte'],
  ['TI', 'Tarjeta de identidad'],
];
const DOCS_EMPRESA = [['NIT', 'NIT']];

export default function SelectorCliente({
  terceros, value, onChange, onCreado, disabled, requerido = true, helperText,
}) {
  const esMovil = useEsMovil();
  const [nuevo, setNuevo] = useState(null);
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);

  const sel = terceros.find((t) => t.id === value) || null;

  const abrirAlta = (nombre) => {
    setError('');
    setNuevo({
      persona: 'natural',
      razon_social: (nombre || '').trim(),
      tipo_documento: 'CC', numero_documento: '',
      telefono: '', email_facturacion: '', direccion_fiscal: '', ciudad: '',
    });
  };

  const guardar = async () => {
    const { persona, ...datos } = nuevo;
    if (!datos.razon_social.trim()) { setError('Falta el nombre.'); return; }
    if (errorTelefono(datos.telefono) || errorCorreo(datos.email_facturacion)) {
      setError(errorTelefono(datos.telefono) || errorCorreo(datos.email_facturacion)); return;
    }
    if (!datos.numero_documento.trim()) {
      setError(persona === 'natural'
        ? 'Falta el número de cédula: sin él no se puede facturar.'
        : 'Falta el NIT: sin él no se puede facturar.');
      return;
    }
    setGuardando(true);
    try {
      const { data } = await api.post('/terceros', {
        ...datos,
        razon_social: datos.razon_social.trim(),
        numero_documento: datos.numero_documento.trim(),
        es_cliente: true, moneda_default: 'COP', pais: 'Colombia',
      });
      await onCreado?.();
      onChange(data.id, data);
      setNuevo(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo crear el cliente');
    } finally { setGuardando(false); }
  };

  const esEmpresa = nuevo?.persona === 'empresa';

  return (
    <>
      <Autocomplete
        freeSolo selectOnFocus handleHomeEndKeys fullWidth size="small"
        disabled={disabled}
        options={terceros}
        value={sel}
        getOptionLabel={(o) => (typeof o === 'string' ? o : o.razon_social)}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        filterOptions={(ops, estado) => {
          const f = filtrar(ops, estado);
          const t = (estado.inputValue || '').trim();
          if (t && !ops.some((o) => o.razon_social.toLowerCase() === t.toLowerCase())) {
            f.push({ _crear: true, razon_social: t });
          }
          return f;
        }}
        renderOption={(props, o) => (
          <li {...props} key={o._crear ? '__crear' : o.id}>
            {o._crear ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75,
                         color: 'primary.main' }}>
                <AddIcon sx={{ fontSize: 16 }} />
                <Typography sx={{ fontSize: 13.5 }}>
                  Crear cliente <strong>{o.razon_social}</strong>
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', width: '100%', gap: 2, alignItems: 'baseline' }}>
                <Typography sx={{ fontSize: 13.5, flexGrow: 1 }}>{o.razon_social}</Typography>
                <Typography sx={{ fontSize: 11, color: 'text.disabled',
                                  whiteSpace: 'nowrap' }}>
                  {o.tipo_documento} {o.numero_documento}
                </Typography>
              </Box>
            )}
          </li>
        )}
        onChange={(_, v) => {
          if (!v) { onChange(null, null); return; }
          if (typeof v === 'string') { abrirAlta(v); return; }
          if (v._crear) { abrirAlta(v.razon_social); return; }
          onChange(v.id, v);
        }}
        renderInput={(params) => (
          <TextField {...params} label={`Cliente${requerido ? ' *' : ''}`}
            helperText={helperText !== undefined ? helperText
              : sel ? `${sel.tipo_documento} ${sel.numero_documento || '—'}`
                : 'A nombre de quien se factura. Si no está, escríbelo y créalo'} />
        )} />

      <Dialog fullScreen={esMovil} open={Boolean(nuevo)} onClose={() => setNuevo(null)} fullWidth maxWidth="sm">
        <DialogTitle>Nuevo cliente</DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {nuevo && (
            <Stack spacing={2} sx={{ pt: 0.5 }}>
              {/* Lo primero, porque cambia todo lo demás: una persona lleva
                  cédula y una empresa NIT. */}
              <ToggleButtonGroup exclusive size="small" fullWidth value={nuevo.persona}
                onChange={(_, v) => v && setNuevo({ ...nuevo, persona: v,
                  tipo_documento: v === 'empresa' ? 'NIT' : 'CC' })}>
                <ToggleButton value="natural">
                  <PersonIcon sx={{ fontSize: 17, mr: 0.75 }} /> Persona natural
                </ToggleButton>
                <ToggleButton value="empresa">
                  <BusinessIcon sx={{ fontSize: 17, mr: 0.75 }} /> Empresa
                </ToggleButton>
              </ToggleButtonGroup>

              <TextField size="small" autoFocus fullWidth
                label={esEmpresa ? 'Razón social *' : 'Nombre completo *'}
                value={nuevo.razon_social}
                onChange={e => setNuevo({ ...nuevo, razon_social: e.target.value })} />

              <Stack direction={FILA} spacing={2}>
                <TextField select size="small" label="Documento" sx={ancho(210)}
                  value={nuevo.tipo_documento}
                  onChange={e => setNuevo({ ...nuevo, tipo_documento: e.target.value })}>
                  {(esEmpresa ? DOCS_EMPRESA : DOCS_NATURAL).map(([v, t]) => (
                    <MenuItem key={v} value={v}>{t}</MenuItem>
                  ))}
                </TextField>
                <TextField size="small" fullWidth
                  label={esEmpresa ? 'NIT *' : 'Número *'}
                  value={nuevo.numero_documento}
                  helperText={esEmpresa ? 'Con dígito de verificación: 900123456-7'
                    : 'Sin puntos ni comas'}
                  onChange={e => setNuevo({ ...nuevo, numero_documento: e.target.value })} />
              </Stack>

              <Stack direction={FILA} spacing={2}>
                <CampoTelefono etiqueta="Teléfono" fullWidth valor={nuevo.telefono}
                  alCambiar={(v) => setNuevo({ ...nuevo, telefono: v })} />
                <CampoCorreo etiqueta="Correo" fullWidth
                  valor={nuevo.email_facturacion}
                  ayuda="A donde llega la factura"
                  alCambiar={(v) => setNuevo({ ...nuevo, email_facturacion: v })} />
              </Stack>

              <Stack direction={FILA} spacing={2}>
                <TextField size="small" label="Dirección" fullWidth
                  value={nuevo.direccion_fiscal}
                  onChange={e => setNuevo({ ...nuevo,
                    direccion_fiscal: e.target.value })} />
                <TextField size="small" label="Ciudad" sx={ancho(200)}
                  value={nuevo.ciudad}
                  onChange={e => setNuevo({ ...nuevo, ciudad: e.target.value })} />
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setNuevo(null)}>Cancelar</Button>
          <Button variant="contained" onClick={guardar} disabled={guardando}>
            Crear y usar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
